import type { ElementType } from "@circle-react/types/Element";
import type { Form } from "@circle-react/types/Form";
import { useToast } from "@circleco/compass/context/CompassContext";
import { useEffect } from "react";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import type { CreateSubmissionPayload } from "@/react/api/submissionsApi";
import { submissionsApi } from "@/react/api/submissionsApi";
import type { ApiError } from "@/react/config/CustomErrors";

import { useFormRenderingContext } from "../contexts/FormRenderingContext";
import { useModalCookie } from "../hooks/useModalCookie";

interface ElementFieldMap {
  [key: number]: number;
}

const traverseElements = (
  elements: ElementType[],
  elementFieldMap: ElementFieldMap,
  elementFieldsIndex: { value: number },
): void => {
  elements.forEach(element => {
    if (element.type === "field" && !element.attributes.hidden) {
      elementFieldMap[element.elementable_id] = elementFieldsIndex.value;
      elementFieldsIndex.value++;
    } else if (element.type === "layout" && element.children) {
      traverseElements(
        element.children as ElementType[],
        elementFieldMap,
        elementFieldsIndex,
      );
    }
  });
};

const elementFieldsToMap = (form: Form): ElementFieldMap => {
  const elementFieldMap: ElementFieldMap = {};
  const elementFieldsIndex = { value: 0 };
  traverseElements(form.elements, elementFieldMap, elementFieldsIndex);
  return elementFieldMap;
};

interface SubmissionResponse {
  success: boolean;
}

interface ErrorDetail {
  elementable_id: number;
  error: string;
}

type ErrorMap = Record<number, string>;

interface UseSubmitFormProps {
  onError?: () => void;
}

export const useSubmitForm = ({ onError }: UseSubmitFormProps = {}) => {
  const toast = useToast();
  const {
    form,
    index,
    isModal,
    usage,
    renderType,
    setBackendErrors,
    setShouldRenderEndingPage,
    setIsSubmitting,
  } = useFormRenderingContext();
  const { suppressPopupForm } = useModalCookie();
  const elementFieldsMap = elementFieldsToMap(form);

  const { mutate, isPending: isSubmitting } = useMutation<
    SubmissionResponse,
    ApiError,
    CreateSubmissionPayload
  >({
    mutationFn: payload => submissionsApi.create({ formId: form.id, payload }),
    onSuccess: () => {
      if (isModal) {
        suppressPopupForm();
      }
      if (form.after_submission_action === "thank_you_page") {
        setShouldRenderEndingPage(true);
      } else if (form.after_submission_action === "redirect") {
        if (renderType === "standalone" || usage === "newsletter") {
          window.location.href = form.redirect_url;
        } else {
          // eslint-disable-next-line sonarjs/post-message -- Disabled for plugin version update. Fix when possible.
          window.parent.postMessage(
            {
              type: "redirect",
              formUid: form.uid,
              formIndex: index,
              url: form.redirect_url,
            },
            "*",
          );
        }
      }
    },
    onError: error => {
      onError?.();

      if (
        error.errorDetails &&
        Array.isArray(error.errorDetails) &&
        error.errorDetails.length > 0
      ) {
        const errors = error.errorDetails.reduce(
          (acc: ErrorMap, errorDetail: ErrorDetail) => {
            acc[elementFieldsMap[errorDetail.elementable_id]] =
              errorDetail.error;
            return acc;
          },
          {},
        ) as ErrorMap;
        setBackendErrors(errors);
      } else if (error.status === 429) {
        toast.add({
          title: t("settings.forms.errors.too_many_submissions"),
          variant: "destructive",
          icon: "triangle-exclamation-filled",
          iconColor: "danger",
        });
      } else {
        toast.add({
          title: error.message,
          variant: "destructive",
          icon: "triangle-exclamation-filled",
          iconColor: "danger",
        });
      }
    },
  });

  useEffect(() => {
    setIsSubmitting(isSubmitting);
  }, [isSubmitting, setIsSubmitting]);

  return { mutate };
};
